import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import './index.scss';
import type { MenuProps } from 'antd';
import { Drawer, Menu, message, Avatar, Dropdown } from 'antd';
import { ThemeModeSelector } from '@/common/components/themed';
import {
  AppstoreOutlined,
  LeftOutlined,
  LogoutOutlined,
  UserOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import {
  HomeIcon,
  CanvasIcon,
  InvestorsIcon,
  PlanningIcon,
  FileHubIcon,
  MeetingIcon,
  SettingsIcon,
  CollapseClosedIcon,
  CollapseOpenIcon,
  LogoIcon,
} from '@/common/components/custom-icons';
import { logout } from '@/api/login.v2';
import { isEnterpriseBilling, userCanManageBilling } from '@/pages/stripe/billing-status';
import {
  removeItemWithExpiry,
  selectNormalBrandLogo,
  selectCompactBrandLogo,
} from '@wavvest/shared-utils';
import { FormModalProvider } from '@/common/components/modal/context';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { useTheme } from '@/contexts/ThemeContext';
import {
  selectApexEnabled,
  selectProfileData,
  resetFeatureFlags,
  resetUser,
  selectUser,
  selectFeatureFlags,
  selectCanAccessPlanner,
  selectCanAccessAdvisorPrepDashboard,
} from '@/store/features/userinfo/userinfoSlice';
import { resetCanvas } from '@/store/features/canvas/canvasSlice';
import { resetInvestors } from '@/store/features/investors/investorsSlice';
import { resetApex } from '@/store/features/userinfo/apexSlice';
import { resetPlaid } from '@/store/features/userinfo/plaidSlice';
import {
  selectActiveV2CategoryName,
  selectActiveV2PromptName,
  selectActiveV2CategoryKey,
} from '@/store/features/planning/planningSlice';
import canvasWebSocket from '@/services/websocket-service';
import { clearSentryUser } from '@/sentry';
import { MenuItemType, MenuItemGroupType } from 'antd/es/menu/interface';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import config from '@/services/config';
import { getGlobalFlag } from '@/api/feature-flags';
import {
  isClientsListRoute,
  isInvestorProfileRoute,
  isMeetingHubRoute,
  isInvestorPlanRoute,
  isScrollableNoHeaderRoute,
  normalizeHomeRoutePathname,
} from './route-utils';
import { isSafeImageUri } from '@/pages/login/brandContext';
import { PlanningNavigationProvider } from './planning-navigation-context';
import { hasInitialAppLoadState } from '@/routes/initial-app-load-state';
import { markAuthRouteLoaderSuppressed } from '@/routes/auth-loader-state';
import { RouteLoadingShellProvider } from '@/routes/components/route-loading-shell-context';

const initWidth = 1920,
  logoWidthRadio = 190 / initWidth;

const collapsedWidth = 56;

const LogoImageWithFallback: React.FC<{
  profileLogoUri: string;
  width: number;
  height: number;
  objectPosition?: string;
}> = ({ profileLogoUri, width, height, objectPosition = 'left center' }) => {
  const [useFallback, setUseFallback] = React.useState(false);

  if (!profileLogoUri || useFallback) {
    return <LogoIcon style={{ width, height, fontSize: width }} />;
  }

  return (
    <img
      src={profileLogoUri}
      alt="Logo"
      style={{ width, height, objectFit: 'contain', objectPosition }}
      onError={() => setUseFallback(true)}
    />
  );
};

const Home: React.FC = () => {
  const { theme, themePreference, setThemePreference } = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = normalizeHomeRoutePathname(location.pathname);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isPlanningNavigationOpen, setIsPlanningNavigationOpen] = useState(false);
  const [maintenanceModeEnabled, setMaintenanceModeEnabled] = useState(false);

  const user = useAppSelector(selectUser);
  const apexEnabled = useAppSelector(selectApexEnabled);
  const profileData = useAppSelector(selectProfileData);
  const sanitizeLogoUri = (uri: string): string => (uri && isSafeImageUri(uri) ? uri : '');
  // Expanded sidebar + top nav use the normal logo; the collapsed sidebar uses
  // the compact logo. Both are theme-aware with cross-fallback.
  const normalLogoUri = sanitizeLogoUri(selectNormalBrandLogo(profileData ?? {}, theme));
  const compactLogoUri = sanitizeLogoUri(selectCompactBrandLogo(profileData ?? {}, theme));
  const canAccessPlanner = useAppSelector(selectCanAccessPlanner);
  const canAccessAdvisorPrepDashboard = useAppSelector(selectCanAccessAdvisorPrepDashboard);
  const activeV2CategoryName = useAppSelector(selectActiveV2CategoryName);
  const activeV2PromptName = useAppSelector(selectActiveV2PromptName);
  const activeV2CategoryKey = useAppSelector(selectActiveV2CategoryKey);

  const isOnPlanningPage =
    pathname.includes('/planning') && !pathname.includes('planning-templates');
  const isDashboardPage = pathname === '/home/dashboard';
  const isAdvisorPrepDashboardPage = pathname === '/home/advisor-prep-dashboard';
  const isClientsListPage = isClientsListRoute(pathname);
  const isMeetingHubPage = isMeetingHubRoute(pathname);
  const isInvestorProfilePage = isInvestorProfileRoute(pathname);
  const isInvestorPlanPage = isInvestorPlanRoute(pathname);
  const isScrollableNoHeaderPage = isScrollableNoHeaderRoute(pathname);
  const hideHeader = isOnPlanningPage || isClientsListPage || isScrollableNoHeaderPage;
  const openPlanningNavigation = useCallback(() => setIsPlanningNavigationOpen(true), []);
  const planningNavigationContext = useMemo(
    () => ({ openNavigation: openPlanningNavigation }),
    [openPlanningNavigation],
  );

  useIdleTimeout({
    timeout: config.idleTimeout,
    warningTime: config.idleWarningTime,
    enabled: true,
  });

  useEffect(() => {
    const fetchMaintenanceMode = async () => {
      try {
        const data = await getGlobalFlag('maintenance-flag');
        setMaintenanceModeEnabled(data.value === 'enabled');
      } catch (error) {
        console.error('Failed to fetch maintenance mode:', error);
      }
    };

    fetchMaintenanceMode();
  }, []);

  useEffect(() => {
    setIsPlanningNavigationOpen(false);
  }, [pathname]);

  type MenuItem = MenuItemType | MenuItemGroupType;

  const menuItems = useMemo(() => {
    const items: MenuItem[] = [
      ...(apexEnabled
        ? [
            {
              key: '/home/dashboard',
              label: 'Home',
              icon: <HomeIcon />,
            },
          ]
        : []),
      ...(canAccessPlanner
        ? [
            {
              key: '/home/planning',
              label: 'Wavvest Planning',
              icon: <CanvasIcon />,
            },
          ]
        : []),
      ...(canAccessPlanner
        ? [
            {
              key: '/home/planning-templates',
              label: 'Planning Templates',
              icon: <PlanningIcon />,
            },
          ]
        : []),
      ...(canAccessAdvisorPrepDashboard
        ? [
            {
              key: '/home/advisor-prep-dashboard',
              label: 'Advisor Prep Dashboard',
              icon: <AppstoreOutlined />,
            },
          ]
        : []),
      {
        key: '/home/clients',
        label: 'Clients',
        icon: <InvestorsIcon />,
      },
      {
        key: '/home/meetinghub',
        label: 'Meeting Hub',
        icon: <MeetingIcon />,
      },
      // { key: "/home/risk-assessment", label: "Risk Assessment" },
      {
        key: '/home/filehub',
        label: 'File Hub',
        icon: <FileHubIcon />,
      },
      {
        key: 'settings',
        label: 'Settings',
        icon: <SettingsIcon />,
        children: [
          {
            key: '/home/profile',
            label: 'Profile',
            // icon: <ProfileFilled />,
          },
          {
            key: '/home/company-settings',
            label: 'Company Settings',
            // icon: <SettingFilled />,
          },
          {
            key: '/home/members',
            label: 'Team Members',
          },
        ],
      },
    ];
    return items;
  }, [apexEnabled, canAccessPlanner, canAccessAdvisorPrepDashboard]);

  // Team Members moved under Settings submenu

  useEffect(() => {
    if (pathname.includes('planning-templates')) {
      setTitle('Planning Templates');
      return;
    }
    if (pathname.includes('/planning')) {
      setTitle('');
      return;
    }
    if (pathname === '/home/investor-financial-plan') {
      setTitle('Clients Financial Plan');
    } else if (pathname === '/home/financial-plan-edit') {
      setTitle('Edit Financial Plan');
    } else if (pathname !== '/home/clients' && pathname.startsWith('/home/clients/')) {
      setTitle('Clients');
    } else if (pathname === '/home/transaction-history') {
      setTitle('Transaction History');
    } else {
      // Handle nested menu items
      let foundItem: MenuItem | undefined;

      const menuItemsList = [...menuItems];
      // First check for direct matches
      foundItem = menuItemsList.find((item) => item.key === pathname);

      // If not found, check in nested children
      if (!foundItem) {
        for (const item of menuItems) {
          if ('children' in item && item.children) {
            const childItem = item.children.find(
              (child) => child && 'key' in child && child.key === pathname,
            );
            if (childItem && 'label' in childItem) {
              foundItem = childItem as MenuItem;
              break;
            }
          }
        }
      }

      let newTitle = 'Default Title';

      if (foundItem) {
        if (typeof foundItem.label === 'string') {
          newTitle = foundItem.label;
        } else if (React.isValidElement(foundItem.label)) {
          const labelElement = foundItem.label as React.ReactElement;
          if (labelElement.props.children) {
            newTitle = labelElement.props.children;
          }
        }
      }

      setTitle(newTitle);
    }
  }, [pathname, menuItems, activeV2CategoryName, activeV2PromptName, activeV2CategoryKey]);

  const handleBackClick = () => {
    navigate(-1);
  };
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const expandedNavWidth = Math.max(230, Math.round(logoWidthRadio * windowSize.width) + 32);
  const navWidth = isCollapsed ? collapsedWidth : expandedNavWidth;

  useEffect(() => {
    const outletContainer = document.querySelector('.outletContainer');
    if (outletContainer) {
      localStorage.setItem('outletHeight', outletContainer.clientHeight.toString());
    }
  }, [windowSize]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const home = document.getElementById('home');
    if (home) {
      home.style.width = `${windowSize.width}px`;
      home.style.height = `${windowSize.height}px`;
    }
  }, [windowSize]);

  // Width is now driven purely by React state + CSS transition
  // (removed direct DOM manipulation that conflicted with CSS transitions)

  const billingFeatures = useAppSelector(selectFeatureFlags);
  const isInitialHomeRoutingHandoff =
    pathname === '/home' && hasInitialAppLoadState(location.state);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key);
    setIsPlanningNavigationOpen(false);
    let clickedItem = menuItems.find((item) => item.key === e.key);

    // If not found directly, search in nested children
    if (!clickedItem) {
      for (const item of menuItems) {
        if ('children' in item && item.children) {
          const childItem = item.children.find(
            (child) => child && 'key' in child && child.key === e.key,
          );
          if (childItem && 'label' in childItem) {
            clickedItem = childItem as MenuItem;
            break;
          }
        }
      }
    }

    if (clickedItem && 'label' in clickedItem) {
      setTitle(clickedItem.label as string);
    }
  };

  const logoutClick = async () => {
    setLoading(true);
    try {
      await logout();
      clearSentryUser();
      removeItemWithExpiry('access_token');
      removeItemWithExpiry('user');
      dispatch(resetFeatureFlags());
      dispatch(resetUser());
      dispatch(resetCanvas());
      dispatch(resetInvestors());
      dispatch(resetApex());
      dispatch(resetPlaid());

      navigate('/login', { replace: true, state: markAuthRouteLoaderSuppressed() });
    } catch (error) {
      messageApi.error('Logout failed');
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
      if (canvasWebSocket.getConnectionStatus()) {
        canvasWebSocket.disconnect();
      }
    }
  };

  const selectedKey =
    pathname === '/home/investor-financial-plan'
      ? '/home/financial-plan-list'
      : pathname === '/home/financial-plan-edit'
        ? '/home/financial-plan-list'
        : isOnPlanningPage
          ? '/home/planning'
          : pathname;

  const getAvatarText = (): string => {
    if (!user || !user.name) return 'U';

    const nameParts = user.name.split(' ');
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    } else {
      return user.name.substring(0, 2).toUpperCase();
    }
  };

  const avatarDropdownItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: (
        <div className="dropdown-item-content">
          <span className="dropdown-icon">
            <UserOutlined />
          </span>
          <span className="dropdown-label">{user?.name || 'User'}</span>
        </div>
      ),
      onClick: () => navigate('/home/profile'),
    },
    {
      key: 'theme',
      label: (
        <div className="dropdown-item-content dropdown-theme-control">
          <span className="dropdown-label">Theme</span>
          <ThemeModeSelector onChange={setThemePreference} value={themePreference} />
        </div>
      ),
    },
    {
      key: 'billing',
      label: (
        <div className="dropdown-item-content">
          <span className="dropdown-icon">
            <DollarOutlined />
          </span>
          <span className="dropdown-label">Manage my billing</span>
        </div>
      ),
      disabled: !userCanManageBilling(user) || isEnterpriseBilling(billingFeatures.stripeInfo),
      onClick: () => navigate('/subscriptions?manage=1'),
    },
    {
      key: 'logout',
      label: (
        <div className="dropdown-item-content">
          <span className="dropdown-icon">
            <LogoutOutlined spin={loading} />
          </span>
          <span className="dropdown-label">Log out</span>
        </div>
      ),
      disabled: loading,
      onClick: logoutClick,
    },
  ];

  const containerClass = `home-container ${theme}-theme${isOnPlanningPage ? ' planning-route' : ''}`;

  if (isInitialHomeRoutingHandoff || isInvestorPlanPage) {
    return <Outlet />;
  }

  return (
    <div className={containerClass} id="home">
      {contextHolder}
      <div className={`navibar${isCollapsed ? ' collapsed' : ''}`} style={{ width: navWidth }}>
        <div className={`logoContainer${isCollapsed ? ' collapsed' : ''}`}>
          <button
            type="button"
            aria-label="Home"
            onClick={() => navigate('/home')}
            className="logo-button"
          >
            <LogoImageWithFallback
              profileLogoUri={isCollapsed ? compactLogoUri : normalLogoUri}
              width={isCollapsed ? 40 : logoWidthRadio * windowSize.width}
              height={isCollapsed ? 33 : logoWidthRadio * windowSize.width * (64 / 237)}
              objectPosition={isCollapsed ? 'center' : 'left center'}
            />
          </button>
          <button
            type="button"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
            className={`collapse-toggle${isCollapsed ? ' collapsed' : ''}`}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? <CollapseClosedIcon /> : <CollapseOpenIcon />}
          </button>
        </div>

        <div
          className="naviList"
          style={{
            paddingLeft: isCollapsed ? 0 : 6,
            paddingRight: isCollapsed ? 0 : 6,
          }}
        >
          <Menu
            selectedKeys={[selectedKey]}
            mode="inline"
            items={menuItems}
            onClick={handleMenuClick}
            inlineCollapsed={isCollapsed}
            rootClassName="sidebar-menu"
            popupClassName="sidebar-menu"
            style={{ background: 'transparent' }}
          />
        </div>

        <div className="sidebar-avatar-section">
          <Dropdown
            menu={{
              items: avatarDropdownItems,
              className: `sidebar-avatar-dropdown sidebar-avatar-dropdown--${theme}`,
            }}
            placement="topRight"
            trigger={['click']}
          >
            <div
              aria-haspopup="menu"
              aria-label="Open user menu"
              className="sidebar-avatar-wrapper"
              role="button"
              tabIndex={0}
            >
              <Avatar
                size={28}
                className="sidebar-avatar"
                style={{
                  backgroundColor: '#c0c0c0',
                  color: '#212121',
                  fontSize: 14,
                  fontWeight: 400,
                  cursor: 'pointer',
                }}
              >
                {getAvatarText()}
              </Avatar>
              <div className="sidebar-avatar-info">
                <span className="sidebar-avatar-name">{user?.name || 'User'}</span>
              </div>
            </div>
          </Dropdown>
        </div>
      </div>
      {isOnPlanningPage && (
        <Drawer
          className="planning-mobile-navigation-drawer"
          placement="left"
          open={isPlanningNavigationOpen}
          onClose={() => setIsPlanningNavigationOpen(false)}
          width={280}
          title={
            <LogoImageWithFallback
              profileLogoUri={normalLogoUri || compactLogoUri}
              width={132}
              height={36}
            />
          }
        >
          <Menu
            selectedKeys={[selectedKey]}
            mode="inline"
            items={menuItems}
            onClick={handleMenuClick}
            rootClassName="sidebar-menu"
            popupClassName="sidebar-menu"
            style={{ background: 'transparent' }}
          />
        </Drawer>
      )}
      <div
        className={`navContent${hideHeader ? ' no-header' : ''}${
          isClientsListPage ? ' clients-route' : ''
        }${isMeetingHubPage ? ' meeting-hub-route' : ''}${
          isScrollableNoHeaderPage ? ' scroll-route' : ''
        }${isInvestorProfilePage ? ' profile-route' : ''}`}
      >
        {maintenanceModeEnabled && (
          <div className="maintenance-banner" role="status" aria-live="polite">
            Maintenance Mode
          </div>
        )}
        {!hideHeader && (
          <div className="header">
            <div className="title">
              {(pathname === '/home/investor-financial-plan' ||
                pathname === '/home/financial-plan-edit') && (
                <LeftOutlined
                  style={{ cursor: 'pointer', marginRight: 10 }}
                  onClick={handleBackClick}
                />
              )}
              {pathname === '/home/investor-financial-plan'
                ? "Client's Financial Plan"
                : pathname === '/home/dashboard'
                  ? `Hi, ${user?.name ?? 'User'}`
                  : title}
            </div>
          </div>
        )}
        <FormModalProvider>
          <div
            className={`outletContainer${
              isDashboardPage || isAdvisorPrepDashboardPage ? ' dashboard-scroll-shell' : ''
            }`}
          >
            <RouteLoadingShellProvider>
              <PlanningNavigationProvider value={planningNavigationContext}>
                <Outlet />
              </PlanningNavigationProvider>
            </RouteLoadingShellProvider>
          </div>
        </FormModalProvider>
      </div>
    </div>
  );
};

export default Home;
